import { CollapsibleCard, Grid, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button, SelectInput } from '@components';
import AddressAutoComplete, {
  AddressDetails,
} from '@components/Autocomplete/AddressAutoComplete';
import {
  CustomerDetailState,
  ICustomerDetails,
} from '@store/customers/customerDetails';
import { CreateCustomerState } from '@store/customers/types';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { sanitizeStateInput } from '@utils/formUtils';
import { handleGooglePlacesSelect, isReadonly } from '@utils/utils';

import { countryList } from '../../constants';
import { addressClasses as classes } from './classes';

type CustomerAddressFormProps = {
  isCreateCustomer?: boolean;
  customerStore: CreateCustomerState | CustomerDetailState;
  customerFormState: (state: Partial<ICustomerDetails>) => void;
  addressType: 'physical' | 'mailing';
  buttonLabel: string;
  setSameAsOther: () => void;
};

const MailingOrPhysicalAddress = (props: CustomerAddressFormProps) => {
  const handleAddressSelect = (place: AddressDetails) => {
    handleGooglePlacesSelect(place, props.customerFormState, props.addressType);
  };

  const isDisabled = () =>
    Boolean(props.isCreateCustomer) ? false : isReadonly();
  return (
    <CollapsibleCard
      startTitle={`${
        props.addressType.charAt(0).toUpperCase() + props.addressType.slice(1)
      } Address`}
      endTitle={
        <Button
          label={props.buttonLabel}
          class={classes.buttonClass}
          onClick={props.setSameAsOther}
          dataTestId={formatDataTestId(props.buttonLabel, 'button')}
        />
      }
      class="mb-2"
    >
      <Grid container spacing={2}>
        <Grid item size={12}>
          <AddressAutoComplete
            label="Address line 1"
            placeholder="Enter Address"
            value={
              props.customerStore.customer[`${props.addressType}Address1`] ?? ''
            }
            onChange={(val) =>
              Boolean(props.customerFormState) &&
              props.customerFormState({ [`${props.addressType}Address1`]: val })
            }
            onItemSelect={handleAddressSelect}
            zIndex="9999"
            disabled={isDisabled()}
            maxLength={50}
          />
        </Grid>
        <Grid item size={12}>
          <TextField
            label="Address line 2"
            placeholder="Enter Address"
            value={
              props.customerStore.customer[`${props.addressType}Address2`] ?? ''
            }
            class={classes.textFields}
            onChange={(val: string) =>
              Boolean(props.customerFormState) &&
              props.customerFormState({
                [`${props.addressType}Address2`]: val,
              })
            }
            disabled={isDisabled()}
            maxLength={50}
            dataTestId={`mailing-or-physical-address-${props.addressType}-address2-textInput`}
          />
        </Grid>
        <Grid container columnSpacing={1}>
          <Grid item size={{ md: 12, lg: 6 }} paddingTop="0px !important">
            <TextField
              label="City"
              value={
                props.customerStore.customer[`${props.addressType}City`] ?? ''
              }
              onChange={(val: string) =>
                Boolean(props.customerFormState) &&
                props.customerFormState({
                  [`${props.addressType}City`]: val,
                })
              }
              disabled={isDisabled()}
              maxLength={50}
              dataTestId={`mailing-or-physical-address-${props.addressType}-city-textInput`}
            />
          </Grid>
          <Grid item size={{ md: 12, lg: 2 }} paddingTop="0px !important">
            <TextField
              label="State"
              value={
                props.customerStore.customer[`${props.addressType}State`] ?? ''
              }
              onChange={(val: string) => {
                const sanitized = sanitizeStateInput(val);
                const current =
                  props.customerStore.customer[`${props.addressType}State`] ??
                  '';
                if (sanitized === current) return;
                props.customerFormState({
                  [`${props.addressType}State`]: sanitized,
                });
              }}
              disabled={isDisabled()}
              maxLength={2}
              dataTestId={`mailing-or-physical-address-${props.addressType}-state-textInput`}
            />
          </Grid>
          <Grid item size={{ md: 12, lg: 4 }} paddingTop="0px !important">
            <TextField
              label="Zip"
              value={
                props.customerStore.customer[`${props.addressType}Zip`] ?? ''
              }
              class={classes.textFields}
              onChange={(val: string) =>
                Boolean(props.customerFormState) &&
                props.customerFormState({
                  [`${props.addressType}Zip`]: val,
                })
              }
              disabled={isDisabled()}
              maxLength={10}
              dataTestId={`mailing-or-physical-address-${props.addressType}-zip-textInput`}
            />
          </Grid>
        </Grid>
        <Grid item size={12} paddingTop="0px !important">
          <SelectInput
            label="Country"
            placeholder="Select Country"
            menuItems={countryList.map((c) => ({
              label: c.label,
              value: String(c.value),
            }))}
            value={
              props.customerStore.customer[`${props.addressType}CountryCode`] ??
              ''
            }
            onChange={(val) =>
              Boolean(props.customerFormState) &&
              props.customerFormState({
                [`${props.addressType}CountryCode`]: val,
              })
            }
            dataTestId={formatDataTestId(
              `${props.addressType} country`,
              'selectInput',
            )}
          />
        </Grid>
      </Grid>
    </CollapsibleCard>
  );
};

export default MailingOrPhysicalAddress;
